import { useEffect, useState } from 'react';

import NotificationsIcon from '@mui/icons-material/Notifications';
import { Grid, ListItemText, Typography } from '@mui/material';
import { Box } from '@mui/system';

import { IOrderType } from '../../../types/OrderOverview';
import { OrderStatus } from '../../constants/OrderStatus';
import {
  infoBlue,
  primaryBlue,
  secondaryBlue,
  successGreen,
  warningRed,
} from '../../constants/colors';
import { DocGenerationSource } from '../../constants/docGenerationSource';
import { OrderTypes } from '../../constants/orderTypeConstants';
import { IAlarmItem, IItemStatus } from '../../type';
import { getWindowDimensions } from '../../utils/helpers';

interface IProps {
  preClosingActive: boolean;
  onClosingActive: boolean;
  postClosingActive: boolean;
  orderStatus?: string;
  orderType: IOrderType;
  isCurrentUserEscrowOnOrder: boolean;
  isCurrentUserNotaryOnOrder?: boolean;
  preClosingSettlementItems: IItemStatus[];
  preClosingNotaryItems?: IItemStatus[];
  onClosingNotaryItems?: IItemStatus[];
  onClosingNotarySubItems?: IItemStatus[];
  onClosingSettlementItems?: IItemStatus[];
  postClosingSettlementItems?: IItemStatus[];
  ipenSession?: any;
}

const initialAlarm: IAlarmItem = {
  title: { name: '', color: '' },
  line1: { name: '', color: primaryBlue },
  line2: { name: '', color: warningRed },
};

function OrdersActionMessageAlarm(props: IProps) {
  const {
    orderType,
    onClosingSettlementItems,
    postClosingSettlementItems,
    preClosingActive,
    onClosingActive,
    postClosingActive,
    orderStatus,
    isCurrentUserEscrowOnOrder,
    isCurrentUserNotaryOnOrder,
    preClosingSettlementItems,
    preClosingNotaryItems,
    onClosingNotaryItems,
    onClosingNotarySubItems,
    ipenSession,
  } = props;
  const [alarmContent, setAlarmContent] = useState<IAlarmItem>(initialAlarm);
  const PrintAllText =
    orderType.DocGen === DocGenerationSource.FA
      ? 'print all documents.'
      : 'print all Ink Sign Documents.';
  const windowSize = getWindowDimensions().width;
  const alarmFontSize = windowSize > 1024 ? 16 : 14;
  const alarmTitleFontSize = windowSize > 1024 ? 19 : 18;

  useEffect(() => {
    if (orderType.Type === OrderTypes.IPEN) {
      setAlarmContent(getIpenAlarmContent());
    } else if (orderType.Type === OrderTypes.HybridEClose) {
      setAlarmContent(getSALPAlarmContent());
    }
  }, [
    preClosingSettlementItems,
    onClosingSettlementItems,
    postClosingSettlementItems,
    preClosingNotaryItems,
    onClosingNotaryItems,
    onClosingNotarySubItems,
    onClosingActive,
    preClosingActive,
    postClosingActive,
    isCurrentUserNotaryOnOrder,
    ipenSession,
  ]);

  const getIpenAlarmContent = () => {
    let newAlarmContent: IAlarmItem = initialAlarm;
    const allSAPreClosingItemsCompleted = preClosingSettlementItems.every(
      (item) => item.completed
    );
    const allNotaryPreClosingItemsCompleted = preClosingNotaryItems?.every(
      (item) => item.completed
    );
    const allNotaryOnClosingItemsCompleted = onClosingNotaryItems?.every(
      (item) => item.completed
    );
    const allNotaryOnClosingSubItemsCompleted = onClosingNotarySubItems?.every(
      (item) => item.completed
    );
    const notaryAssigned = preClosingSettlementItems[3]?.completed;
    const orderHasWitnessSigner =
      onClosingNotaryItems?.find(
        (item) => item.name === 'Identify Witness Parties *'
      ) ?? false;
    const identifyWitnessCompleted =
      onClosingNotaryItems?.find(
        (item) => item.name === 'Identify Witness Parties *'
      )?.completed ?? false;
    const allSigned = ipenSession.isSessionComplete;

    if (
      orderStatus === OrderStatus.Open ||
      orderStatus === OrderStatus.OpenSubmitted
    ) {
      newAlarmContent = {
        title: {
          name: '',
          color: infoBlue,
        },
        line1: {
          name: 'Please wait...',
          color: primaryBlue,
        },
      };
      return newAlarmContent;
    } else if (orderStatus === OrderStatus.Canceled) {
      newAlarmContent = {
        title: {
          name: '',
          color: infoBlue,
        },
        line1: {
          name: 'The order has been canceled.',
          color: primaryBlue,
        },
      };
      return newAlarmContent;
    } else if (
      orderStatus === OrderStatus.Failed ||
      orderStatus === OrderStatus.OpenFailed
    ) {
      newAlarmContent = {
        title: {
          name: '',
          color: warningRed,
        },
        line1: {
          name: 'Oh no, order failed!',
          color: warningRed,
        },
      };
      return newAlarmContent;
    } else if (
      orderStatus === OrderStatus.Archived ||
      orderStatus === OrderStatus.ArchivedAllPreviewed ||
      orderStatus === OrderStatus.ArchivedAllSigned ||
      orderStatus === OrderStatus.ArchivedPending
    ) {
      newAlarmContent = {
        title: {
          name: '',
          color: infoBlue,
        },
        line1: {
          name: 'order archived!',
          color: primaryBlue,
        },
      };
      return newAlarmContent;
    } else if (!isCurrentUserEscrowOnOrder && !isCurrentUserNotaryOnOrder) {
      newAlarmContent = {
        title: {
          name: '',
          color: infoBlue,
        },
        line1: {
          name: 'Settlement agent and Notary will take care of this order.',
          color: primaryBlue,
        },
      };
    } else if (preClosingActive) {
      if (isCurrentUserEscrowOnOrder && !isCurrentUserNotaryOnOrder) {
        if (!notaryAssigned && !allSAPreClosingItemsCompleted) {
          newAlarmContent = {
            title: {
              name: 'Action Required:',
              color: warningRed,
            },
            line2: {
              name: 'Please leverage the interactive checklist below by clicking on each task to mark it as complete.',
              color: primaryBlue,
            },
            line1: {
              name: 'Please assign a Notary as soon as possible. IPEN Orders cannot be serviced without a Notary.',
              color: warningRed,
            },
          };
        } else if (notaryAssigned && !allSAPreClosingItemsCompleted) {
          newAlarmContent = {
            title: {
              name: '',
              color: infoBlue,
            },
            line1: {
              name: 'Please leverage the interactive checklist below by clicking on each task to mark it as complete.',
              color: primaryBlue,
            },
          };
        } else if (notaryAssigned && allSAPreClosingItemsCompleted) {
          newAlarmContent = {
            title: {
              name: '',
              color: infoBlue,
            },
            line1: {
              name: 'All Pre-Closing actions are now complete. You can upload additional documents or change the notary assigned to this order up until the IPEN Session begins.',
              color: primaryBlue,
            },
            line2: {
              name: 'The Notary will launch the IPEN Session on closing day.',
              color: primaryBlue,
            },
          };
        }
      } else if (isCurrentUserNotaryOnOrder && !isCurrentUserEscrowOnOrder) {
        if (!allNotaryPreClosingItemsCompleted) {
          newAlarmContent = {
            title: {
              name: '',
              color: infoBlue,
            },
            line1: {
              name: 'Please leverage the interactive checklist below by clicking on each task to mark it as complete.',
              color: primaryBlue,
            },
          };
        } else {
          newAlarmContent = {
            title: {
              name: '',
              color: infoBlue,
            },
            line1: {
              name: 'You currently do not have any actions to take; Launch IPEN Session will be available on closing day.',
              color: primaryBlue,
            },
          };
        }
      } else if (isCurrentUserNotaryOnOrder && isCurrentUserEscrowOnOrder) {
        if (!allSAPreClosingItemsCompleted) {
          newAlarmContent = {
            title: {
              name: '',
              color: infoBlue,
            },
            line1: {
              name: 'Please leverage the interactive checklist below by clicking on each task to mark it as complete.',
              color: primaryBlue,
            },
          };
        } else {
          newAlarmContent = {
            title: {
              name: '',
              color: infoBlue,
            },
            line1: {
              name: 'You currently do not have any actions to take; Launch IPEN Session will be available on closing day.',
              color: primaryBlue,
            },
          };
        }
      }
    } else if (onClosingActive) {
      if (isCurrentUserEscrowOnOrder && !isCurrentUserNotaryOnOrder) {
        if (!notaryAssigned && !allSAPreClosingItemsCompleted && !allSigned) {
          newAlarmContent = {
            title: {
              name: 'Action Required:',
              color: warningRed,
            },
            line1: {
              name: 'Please leverage the interactive checklist below by clicking on each task to mark it as complete.',
              color: primaryBlue,
            },
            line2: {
              name: 'Please assign a Notary as soon as possible. IPEN Orders cannot be serviced without a Notary.',
              color: warningRed,
            },
          };
        } else if (
          notaryAssigned &&
          !allSAPreClosingItemsCompleted &&
          !allSigned
        ) {
          newAlarmContent = {
            title: {
              name: '',
              color: infoBlue,
            },
            line1: {
              name: 'Today is closing day. Please complete the pre-closing actions before the Notary initiates the IPEN Session.',
              color: primaryBlue,
            },
            line2: {
              name: 'Please leverage the interactive checklist below by clicking on each task to mark it as complete.',
              color: primaryBlue,
            },
          };
        } else if (
          notaryAssigned &&
          !allSAPreClosingItemsCompleted &&
          allSigned
        ) {
          newAlarmContent = {
            title: {
              name: '',
              color: successGreen,
            },
            line1: {
              name: 'All done. You have no additional actions to take.',
              color: successGreen,
            },
          };
        } else if (
          notaryAssigned &&
          allSAPreClosingItemsCompleted &&
          !allSigned
        ) {
          newAlarmContent = {
            title: {
              name: '',
              color: infoBlue,
            },
            line1: {
              name: 'Today is closing day. You have no additional actions to take. The Notary will initiate the IPEN session.',
              color: primaryBlue,
            },
          };
        } else if (
          notaryAssigned &&
          allSAPreClosingItemsCompleted &&
          allSigned
        ) {
          newAlarmContent = {
            title: {
              name: '',
              color: successGreen,
            },
            line1: {
              name: 'All done. The order has been successfully serviced.',
              color: successGreen,
            },
          };
        }
      } else if (isCurrentUserNotaryOnOrder && !isCurrentUserEscrowOnOrder) {
        if (
          !allNotaryOnClosingItemsCompleted ||
          !allNotaryOnClosingSubItemsCompleted
        ) {
          if (ipenSession.isActiveSession) {
            newAlarmContent = {
              title: {
                name: 'Action Required:',
                color: warningRed,
              },
              line1: {
                name: 'Please click “View IPEN Session” below to continue the IPEN session.',
                color: warningRed,
              },
            };
          } else if (orderHasWitnessSigner && !identifyWitnessCompleted) {
            newAlarmContent = {
              title: {
                name: 'Action Required:',
                color: warningRed,
              },
              line2: {
                name: 'Please identify all witness parties before launching the IPEN session.',
                color: warningRed,
              },
              line1: {
                name: 'Please leverage the interactive checklist below by clicking on each task to mark it as complete.',
                color: primaryBlue,
              },
            };
          } else {
            newAlarmContent = {
              title: {
                name: 'Action Required:',
                color: warningRed,
              },
              line1: {
                name: 'Please click “Launch IPEN Session” below to initiate the IPEN session.',
                color: warningRed,
              },
            };
          }
        } else if (allNotaryOnClosingItemsCompleted) {
          newAlarmContent = {
            title: {
              name: '',
              color: successGreen,
            },
            line1: {
              name: 'All done. You have successfully completed the IPEN session.',
              color: successGreen,
            },
          };
        }
      } else if (isCurrentUserEscrowOnOrder && isCurrentUserNotaryOnOrder) {
        if (!notaryAssigned && !allSAPreClosingItemsCompleted) {
          newAlarmContent = {
            title: {
              name: 'Action Required:',
              color: warningRed,
            },
            line1: {
              name: 'Please leverage the interactive checklist below by clicking on each task to mark it as complete.',
              color: primaryBlue,
            },
            line2: {
              name: 'Please assign a Notary as soon as possible. IPEN Orders cannot be serviced without a Notary.',
              color: warningRed,
            },
          };
        } else if (
          notaryAssigned &&
          !allSAPreClosingItemsCompleted &&
          !allSigned &&
          (!allNotaryOnClosingItemsCompleted ||
            !allNotaryOnClosingSubItemsCompleted)
        ) {
          if (ipenSession.isActiveSession) {
            newAlarmContent = {
              title: {
                name: 'Action Required:',
                color: warningRed,
              },
              line1: {
                name: 'Please leverage the interactive checklist below by clicking on each task to mark it as complete.',
                color: primaryBlue,
              },
              line2: {
                name: 'Please click “View IPEN Session” below to continue the IPEN session.',
                color: warningRed,
              },
            };
          } else if (orderHasWitnessSigner && !identifyWitnessCompleted) {
            newAlarmContent = {
              title: {
                name: 'Action Required:',
                color: warningRed,
              },
              line2: {
                name: 'Please identify all witness parties before launching the IPEN session.',
                color: warningRed,
              },
              line1: {
                name: 'Please leverage the interactive checklist below by clicking on each task to mark it as complete.',
                color: primaryBlue,
              },
            };
          } else {
            newAlarmContent = {
              title: {
                name: 'Action Required:',
                color: warningRed,
              },
              line2: {
                name: 'Please click “Launch IPEN Session” below to initiate the IPEN session.',
                color: warningRed,
              },
              line1: {
                name: 'Please leverage the interactive checklist below by clicking on each task to mark it as complete.',
                color: primaryBlue,
              },
            };
          }
        } else if (
          notaryAssigned &&
          allSAPreClosingItemsCompleted &&
          (!allNotaryOnClosingItemsCompleted ||
            !allNotaryOnClosingSubItemsCompleted)
        ) {
          if (ipenSession.isActiveSession) {
            newAlarmContent = {
              title: {
                name: 'Action Required:',
                color: warningRed,
              },
              line1: {
                name: 'Please click “View IPEN Session” below to continue the IPEN session.',
                color: warningRed,
              },
            };
          } else if (orderHasWitnessSigner && !identifyWitnessCompleted) {
            newAlarmContent = {
              title: {
                name: 'Action Required:',
                color: warningRed,
              },
              line2: {
                name: 'Please identify all witness parties before launching the IPEN session.',
                color: warningRed,
              },
              line1: {
                name: 'Please leverage the interactive checklist below by clicking on each task to mark it as complete.',
                color: primaryBlue,
              },
            };
          } else {
            newAlarmContent = {
              title: {
                name: 'Action Required:',
                color: warningRed,
              },
              line1: {
                name: 'Please click “Launch IPEN Session” below to initiate the IPEN session.',
                color: warningRed,
              },
            };
          }
        } else if (
          notaryAssigned &&
          allSigned &&
          allNotaryOnClosingItemsCompleted &&
          allNotaryOnClosingSubItemsCompleted
        ) {
          newAlarmContent = {
            title: {
              name: '',
              color: successGreen,
            },
            line1: {
              name: 'All done. You have successfully completed the IPEN session.',
              color: successGreen,
            },
          };
        }
      }
    } else if (postClosingActive) {
      if (isCurrentUserEscrowOnOrder) {
        if (notaryAssigned && allSigned) {
          newAlarmContent = {
            title: {
              name: '',
              color: successGreen,
            },
            line1: {
              name: 'All done. The order has been successfully serviced.',
              color: successGreen,
            },
          };
        } else if (!notaryAssigned) {
          newAlarmContent = {
            title: {
              name: '',
              color: warningRed,
            },
            line1: {
              name: 'It is past closing date. A notary was not assigned. The order cannot be serviced.',
              color: warningRed,
            },
          };
        } else if (allSAPreClosingItemsCompleted) {
          newAlarmContent = {
            title: {
              name: '',
              color: primaryBlue,
            },
            line1: {
              name: 'It is past closing date. You have no additional actions to take.',
              color: primaryBlue,
            },
          };
        } else {
          newAlarmContent = {
            title: {
              name: '',
              color: primaryBlue,
            },
            line1: {
              name: 'It is past closing date.',
              color: primaryBlue,
            },
          };
        }
      } else if (isCurrentUserNotaryOnOrder) {
        newAlarmContent = {
          title: {
            name: '',
            color: primaryBlue,
          },
          line1: {
            name: 'It is past closing date.',
            color: primaryBlue,
          },
        };
      }
    }
    return newAlarmContent;
  };

  const getSALPAlarmContent = () => {
    let newAlarmContent: IAlarmItem = initialAlarm;
    const allPreClosingItemsCompleted = preClosingSettlementItems.every(
      (item) => item.completed
    );
    const allOnClosingItemsCompleted = onClosingSettlementItems?.every(
      (item) => item.completed
    );
    const allPostItemsCompleted = postClosingSettlementItems?.every(
      (item) => item.completed
    );
    const facilitatedSigningIsDone = onClosingSettlementItems?.find(
      (n) => n.name === 'Facilitate signing of Ink Sign Documents *'
    )?.completed;
    const uploadInkSignIsDone = postClosingSettlementItems?.find(
      (n) => n.name === 'Upload Ink Sign Document to vault *'
    )?.completed;

    if (
      orderStatus === OrderStatus.Open ||
      orderStatus === OrderStatus.OpenSubmitted
    ) {
      newAlarmContent = {
        title: {
          name: '',
          color: primaryBlue,
        },
        line1: {
          name: 'Please wait ...',
          color: primaryBlue,
        },
      };
      return newAlarmContent;
    } else if (orderStatus === OrderStatus.Canceled) {
      newAlarmContent = {
        title: {
          name: '',
          color: infoBlue,
        },
        line1: {
          name: 'The order has been canceled.',
          color: primaryBlue,
        },
      };
      return newAlarmContent;
    } else if (
      orderStatus === OrderStatus.Failed ||
      orderStatus === OrderStatus.OpenFailed
    ) {
      newAlarmContent = {
        title: {
          name: '',
          color: warningRed,
        },
        line1: {
          name: 'Oh no, order failed!',
          color: warningRed,
        },
      };
      return newAlarmContent;
    } else if (
      orderStatus === OrderStatus.Archived ||
      orderStatus === OrderStatus.ArchivedAllPreviewed ||
      orderStatus === OrderStatus.ArchivedAllSigned ||
      orderStatus === OrderStatus.ArchivedPending
    ) {
      newAlarmContent = {
        title: {
          name: '',
          color: infoBlue,
        },
        line1: {
          name: 'order archived!',
          color: primaryBlue,
        },
      };
      return newAlarmContent;
    } else if (preClosingActive) {
      if (allPreClosingItemsCompleted) {
        newAlarmContent = {
          title: {
            name: '',
            color: infoBlue,
          },
          line1: {
            name: 'All Pre-Closing actions are now complete. You can upload additional docs.',
            color: primaryBlue,
          },
        };
      } else {
        newAlarmContent = {
          title: {
            name: '',
            color: infoBlue,
          },
          line1: {
            name: 'Please leverage the interactive checklist below by clicking on each task to mark it as complete.',
            color: primaryBlue,
          },
        };
      }
    } else if (onClosingActive) {
      if (allOnClosingItemsCompleted && !allPreClosingItemsCompleted) {
        newAlarmContent = {
          title: {
            name: '',
            color: infoBlue,
          },
          line1: {
            name: 'Today is closing day. All On-Closing actions are now complete.',
            color: primaryBlue,
          },
          line2: {
            name: 'Please leverage the interactive Pre-Closing checklist by clicking on each task to mark it as complete.',
            color: primaryBlue,
          },
        };
      } else if (!allOnClosingItemsCompleted && allPreClosingItemsCompleted) {
        newAlarmContent = {
          title: {
            name: '',
            color: infoBlue,
          },
          line1: {
            name: 'Today is closing day. All Pre-Closing actions are now complete.',
            color: primaryBlue,
          },
          line2: {
            name: 'Please leverage the interactive On-Closing checklist by clicking on each task to mark it as complete.',
            color: primaryBlue,
          },
        };
      } else if (
        allOnClosingItemsCompleted &&
        allPreClosingItemsCompleted &&
        !allPostItemsCompleted
      ) {
        newAlarmContent = {
          title: {
            name: '',
            color: infoBlue,
          },
          line1: {
            name: 'Today is closing day. All Pre-Closing and On-Closing actions are now complete.',
            color: primaryBlue,
          },
        };
      } else if (
        allOnClosingItemsCompleted &&
        allPreClosingItemsCompleted &&
        allPostItemsCompleted
      ) {
        newAlarmContent = {
          title: {
            name: '',
            color: successGreen,
          },
          line1: {
            name: 'All done. Good job!',
            color: successGreen,
          },
        };
      } else if (!allOnClosingItemsCompleted && !allPreClosingItemsCompleted) {
        if (!facilitatedSigningIsDone && uploadInkSignIsDone) {
          newAlarmContent = {
            title: {
              name: 'Action Required:',
              color: warningRed,
            },
            line1: {
              name: 'Today is closing day. Please leverage the interactive Pre-Closing checklist by clicking on each task to mark it as complete.',
              color: primaryBlue,
            },
            line2: {
              name: 'Please acknowledge that you have facilitated signing of Ink Sign document.',
              color: warningRed,
            },
          };
        } else if (
          facilitatedSigningIsDone &&
          !uploadInkSignIsDone &&
          orderType.DocGen === DocGenerationSource.CX
        ) {
          newAlarmContent = {
            title: {
              name: 'Action Required:',
              color: warningRed,
            },
            line1: {
              name: 'Today is closing day. Please leverage the interactive Pre-Closing checklist by clicking on each task to mark it as complete.',
              color: primaryBlue,
            },
            line2: {
              name: 'Please upload fully executed Ink Sign Document to the vault.',
              color: warningRed,
            },
          };
        } else if (!facilitatedSigningIsDone && !uploadInkSignIsDone) {
          newAlarmContent = {
            title: {
              name: 'Action Required:',
              color: warningRed,
            },
            line1: {
              name: 'Please acknowledge that you have facilitated signing of Ink Sign document.',
              color: warningRed,
            },
            line2: {
              name:
                orderType.DocGen === DocGenerationSource.CX
                  ? 'Please upload fully executed Ink Sign Document to the vault.'
                  : '',
              color: warningRed,
            },
          };
        } else {
          newAlarmContent = {
            title: {
              name: '',
              color: infoBlue,
            },
            line1: {
              name: 'Today is closing day. Please leverage the interactive Pre-Closing checklist by clicking on each task to mark it as complete.',
              color: primaryBlue,
            },
          };
        }
      }
    } else if (postClosingActive) {
      if (
        allPreClosingItemsCompleted &&
        allOnClosingItemsCompleted &&
        allPostItemsCompleted
      ) {
        newAlarmContent = {
          title: {
            name: '',
            color: successGreen,
          },
          line1: {
            name: 'All done. Good job!',
            color: successGreen,
          },
        };
      } else if (!allPostItemsCompleted) {
        newAlarmContent = {
          title: {
            name: '',
            color: infoBlue,
          },
          line1: {
            name: 'Please upload Ink Sign Documents to the vault to complete this order.',
            color: primaryBlue,
          },
        };
      }
    }

    if (
      preClosingSettlementItems &&
      preClosingSettlementItems[3] &&
      !preClosingSettlementItems[3].completed
    ) {
      let updatedAlarm: IAlarmItem = newAlarmContent;
      updatedAlarm = {
        ...newAlarmContent,
        title: {
          name: 'Action Required:',
          color: warningRed,
        },
        line2: {
          name: 'Please ' + PrintAllText,
          color: warningRed,
        },
      };

      return updatedAlarm;
    } else if (
      isCurrentUserEscrowOnOrder &&
      orderStatus === OrderStatus.OpenAllSigned
    ) {
      if (!facilitatedSigningIsDone && uploadInkSignIsDone) {
        newAlarmContent = {
          title: {
            name: 'Action Required:',
            color: warningRed,
          },
          line1: {
            name: 'Please acknowledge that you have facilitated signing of Ink Sign document.',
            color: warningRed,
          },
        };
      } else if (
        facilitatedSigningIsDone &&
        !uploadInkSignIsDone &&
        orderType.DocGen === DocGenerationSource.CX
      ) {
        newAlarmContent = {
          title: {
            name: 'Action Required:',
            color: warningRed,
          },
          line1: {
            name: 'Please upload fully executed Ink Sign Document to the vault.',
            color: warningRed,
          },
        };
      } else if (!facilitatedSigningIsDone && !uploadInkSignIsDone) {
        newAlarmContent = {
          title: {
            name: 'Action Required:',
            color: warningRed,
          },
          line1: {
            name: 'Please acknowledge that you have facilitated signing of the Ink Sign document.',
            color: warningRed,
          },
          line2: {
            name:
              orderType.DocGen === DocGenerationSource.CX
                ? 'Please upload fully executed Ink Sign document to the vault.'
                : '',
            color: warningRed,
          },
        };
      }
    }

    return newAlarmContent;
  };

  return (
    <Grid
      container
      sx={{
        paddingTop: 2.5,
        paddingBottom: 2.5,
        backgroundColor: secondaryBlue,
        alignItems: 'center',
        justifyContent: 'center',
        display: 'flex',
      }}>
      <Grid item xs={12} sx={{ display: 'flex', marginLeft: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 1,
          }}>
          <NotificationsIcon
            fontSize='medium'
            sx={{ color: alarmContent.title.color, marginRight: 1 }}
          />
          <Typography
            color={alarmContent.title.color}
            sx={{
              fontSize: alarmTitleFontSize,
              marginRight: alarmContent.title.name != '' ? 4 : 3,
            }}>
            <strong>{alarmContent.title.name}</strong>
          </Typography>
        </Box>
        <div
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 3,
          }}>
          <ListItemText
            primary={<strong>{alarmContent.line1.name}</strong>}
            sx={{
              color: alarmContent.line1.color,
              fontSize: alarmFontSize,
              display: alarmContent?.line2 ? 'list-item' : 'inline-list-item',
            }}
          />
          {alarmContent.line2 && alarmContent?.line2?.name != '' && (
            <ListItemText
              primary={<strong>{alarmContent.line2.name}</strong>}
              sx={{
                color: alarmContent.line2.color,
                fontSize: alarmFontSize,
                display: alarmContent?.line2 ? 'list-item' : 'inline-list-item',
              }}
            />
          )}
        </div>
      </Grid>
    </Grid>
  );
}

export default OrdersActionMessageAlarm;
